// services/notifications/notificationsApi.ts

import baseApi from "../../lib/baseApi";
import {
  NotificationDTO,
  UnreadCountDTO,
  NotificationPreferenceDTO,
  NotificationPreferencePatchDTO,
  Paginated,
} from "../../types/notifications/notificationsTypes";

export const notificationsApi = {
  /**
   * Get paginated notifications for current user.
   * - default: page 1, 20 items per page
   * - returns DRF-style Paginated<NotificationDTO>
   */
  list: async (page: number = 1, pageSize: number = 20) => {
    const { data } = await baseApi.get<Paginated<NotificationDTO>>(
      "/notifications/",
      {
        params: {
          page,
          page_size: pageSize,
        },
      }
    );

    return data;
  },

  /**
   * Get current unread notifications count.
   */
  unreadCount: async () => {
    const { data } = await baseApi.get<UnreadCountDTO>(
      "/notifications/unread_count/"
    );

    return data;
  },

  /**
   * Mark a single notification as read.
   */
  markRead: async (id: number) => {
    const { data } = await baseApi.patch<NotificationDTO>(
      `/notifications/${id}/mark_read/`,
      {}
    );

    return data;
  },

  /**
   * Mark all unread notifications as read.
   */
  markAllRead: async () => {
    const { data } = await baseApi.post<{ updated: number }>(
      "/notifications/mark_all_read/",
      {}
    );

    return data;
  },

  /**
   * List notification preferences of current user.
   */
  listPreferences: async () => {
    const { data } = await baseApi.get<NotificationPreferenceDTO[]>(
      "/notification-preferences/"
    );

    return data;
  },

  /**
   * Update a single notification preference by id.
   *
   * Important:
   * - Do not send the whole preference DTO.
   * - Only send explicit mutable fields.
   * - Backend ignores/strips unsupported channels, but frontend also avoids
   *   sending email_enabled for email_supported=false items.
   */
  updatePreference: async (
    id: number,
    patch: NotificationPreferencePatchDTO
  ) => {
    const { data } = await baseApi.patch<NotificationPreferenceDTO>(
      `/notification-preferences/${id}/`,
      patch
    );

    return data;
  },

  /**
   * Reset all preferences to default.
   */
  resetPreferences: async () => {
    const { data } = await baseApi.post<NotificationPreferenceDTO[]>(
      "/notification-preferences/reset_defaults/",
      {}
    );

    return data;
  },
};